/**
 * EmergencyContactRow — one row of the Emergency Contacts section.
 * Switches between read view and inline-edit form by the row's
 * `isEdit` flag (parent controls).
 *
 * Flutter parity:
 *   - `emergency_contacts_widget.dart` (read view + row actions)
 *   - `edit_emergency_contact_widget.dart` (inline edit form)
 *   - `create_emeregency_contact_widget.dart` [sic — Flutter typo]
 *
 * Read view: avatar (initials) · name · relation · phone · isProxy
 * badge · Edit/Remove buttons.
 *
 * Edit view: First/Middle/Last + Relation typeahead + Phone +
 * "Primary emergency contact" toggle + Done/Remove/Cancel buttons.
 */

import { Pencil, Trash2, X } from 'lucide-react';
import { Button, StatusChip } from '@scripsteam/scrips-signal-ds';
import { RecordRowCard, RecordRowLine } from '@/shared/ui/components/record-row-card';
import { cn } from '@/lib/utils';
import {
  EmergencyContactEditFields,
  isEmergencyContactSaveEnabled,
  missingEmergencyContactFields,
} from './emergency-contact-edit-fields';
import type { EmergencyContactRow as Row } from './emergency-contact-schema';

export interface EmergencyContactRowProps {
  row: Row;
  /** True when this is the only non-deleted row (Remove disabled). */
  isOnly: boolean;
  readOnly?: boolean;
  onChange: (next: Row) => void;
  onCancel: () => void;
  onRemove: () => void;
}

export function EmergencyContactRowComponent({
  row,
  isOnly,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: EmergencyContactRowProps) {
  if (row.isDeleted) return null;
  return row.isEdit ? (
    <EditCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onChange={onChange}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  ) : (
    <ReadCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onEdit={() => onChange({ ...row, isEdit: true })}
      onRemove={onRemove}
    />
  );
}

// The approved design's PaRecordRowCard: the name with the Primary chip, "relation · phone" as the quiet line, edit and
// remove on the right. Same buttons, handlers and disabled rule as before.
function ReadCard({
  row,
  isOnly,
  readOnly,
  onEdit,
  onRemove,
}: {
  row: Row;
  isOnly: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const fullName = [row.firstName, row.middleName, row.lastName]
    .filter((p) => p && p.trim())
    .join(' ') || 'Unnamed contact';

  return (
    <RecordRowCard
      title={fullName}
      chips={
        row.isProxy ? (
          <StatusChip semantic="info" tone="soft">
            Primary
          </StatusChip>
        ) : null
      }
      lines={
        <RecordRowLine>
          {row.relation.name ?? row.relationship ?? '—'} · {row.contactNumber || '—'}
        </RecordRowLine>
      }
      actions={
        !readOnly ? (
          <>
            <button type="button" onClick={onEdit} className="icon-action" aria-label="Edit emergency contact">
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={isOnly}
              className={cn('icon-action', isOnly && 'opacity-40 cursor-not-allowed hover:bg-transparent')}
              aria-label="Remove emergency contact"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        ) : null
      }
    />
  );
}

// The row opened into its fields (design PaRecordRowCard `editing`): Done (primary) then Remove (tertiary), and the
// "Still needed" line after them while Done is off.
function EditCard({
  row,
  isOnly,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: {
  row: Row;
  isOnly: boolean;
  readOnly?: boolean;
  onChange: (next: Row) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  return (
    <RecordRowCard
      editing
      title={row.emergencyContactId ? 'Edit emergency contact' : 'New emergency contact'}
      actions={
        <button type="button" onClick={onCancel} className="icon-action" aria-label="Cancel edit">
          <X className="size-4" />
        </button>
      }
      editActions={
        <>
          <Button
            type="button"
            size="sm"
            onClick={() => onChange({ ...row, isEdit: false })}
            disabled={readOnly || !isEmergencyContactSaveEnabled(row)}
          >
            Done
          </Button>
          <Button type="button" variant="tertiary" size="sm" onClick={onRemove} disabled={isOnly || readOnly}>
            Remove
          </Button>
          {/* PROD-1988: a disabled Done used to explain nothing — Andrew filled every field marked
              required and could not tell that the relationship still needed picking from the list
              (free text does not set `relation.id`, which the save rule requires). */}
          {!readOnly && !isEmergencyContactSaveEnabled(row) ? (
            <span data-slot="record-row-edit-note">
              Still needed: {missingEmergencyContactFields(row).join(', ')}
            </span>
          ) : null}
        </>
      }
    >
      <EmergencyContactEditFields row={row} readOnly={readOnly} onChange={onChange} />
    </RecordRowCard>
  );
}
